import type { ComponentType } from 'react';

import { SELECTABLE_PILL, SELECTABLE_PILL_OFF, SELECTABLE_PILL_ON } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

interface DiscoverPillProps {
  label: string;
  icon?: ComponentType<{ className?: string; weight?: 'regular' | 'fill' | 'duotone' }>;
  selected: boolean;
  onToggle: () => void;
}

/**
 * A toggleable taxonomy pill — "What you cover" in `FirstVisitModal`, step 2
 * of `AddBrandDialog`, and the Output section of `NewWorkflowDialog` share
 * this exact shape rather than each hand-rolling their own selected/
 * unselected chip styling.
 *
 * Renders `SELECTABLE_PILL` from `ui-tokens.ts` — the app's one canonical
 * selection-toggle recipe — rather than a hand-retyped copy. The retyped copy
 * this replaced dropped `justify-center` and the entire
 * `focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`
 * suffix, so keyboard users could toggle these pills with no visible focus
 * ring (the same accessibility bug `TURN_FOOTER_CHIP`'s own comment in
 * `ui-tokens.ts` documents fixing elsewhere).
 */
export function DiscoverPill({ label, icon: Icon, selected, onToggle }: DiscoverPillProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(SELECTABLE_PILL, selected ? SELECTABLE_PILL_ON : SELECTABLE_PILL_OFF)}
    >
      {Icon && <Icon weight="regular" className="size-3.5" />}
      {label}
    </button>
  );
}
